const React = require('react');
const { useEffect, useState, useRef } = React;
const { default: Styled } = require('styled-components');
const { context } = require('app-context');
const { useAppSelector } = require('utils/redux');
const { default: TextField } = require('@mui/material/TextField');
const { default: Button } = require('@mui/material/Button');
const { Link, useLocation } = require('react-router-dom');
const { default: ArrowBack } = require('@mui/icons-material/ArrowBack');

const Ui = require('components/Gamification/styles');

const internals = {};

module.exports = function InviteFriendsPage() {

    const user = useAppSelector(context.selectors.all.getCurrentUserObject);
    const schoolId = useAppSelector(context.selectors.all.getCurrentSchoolId);
    const location = useLocation();
    const freeCommunity = location.pathname === '/app/community-invite-friends';
    const referralApi = freeCommunity ? context.api.nearpeer.communityLaunch : context.api.nearpeer.gamification;
    const fromProfile = location.state?.fromProfile;
    const backPath = fromProfile === `/app/profile/${user?.id}` ? fromProfile : '/app/welcome';
    const back = <Button component={Link} to={backPath} startIcon={<ArrowBack />} aria-label={backPath === '/app/welcome' ? 'Back to Home' : 'Back to your profile'}>Back</Button>;

    const eligible = freeCommunity ? [2, 12].includes(user?.roleId) : Number(schoolId || user?.schoolId) === 240394 && user?.roleId === 2;
    const activeScope = useRef('');
    activeScope.current = `${freeCommunity}:${schoolId || user?.schoolId}:${user?.id}:${user?.roleId}`;
    const [referrals, setReferrals] = useState(null);
    const [email, setEmail] = useState('');
    const [roleId, setRoleId] = useState('');
    const [busy, setBusy] = useState(false);
    const [message, setMessage] = useState('');
    const { Panel, Actions, EmailField, RoleField } = internals;
    useEffect(() => {

        let current = true;
        setReferrals(null);
        setMessage('');
        setBusy(false);
        setEmail('');
        setRoleId('');
        if (eligible) {
            const requestedScope = activeScope.current;
            referralApi.referrals().then((data) => {

                if (!current) { return; }
                setReferrals({ ...data, scope: requestedScope });
                setRoleId(String(data.roles?.[0]?.id || ''));
            }).catch(() => current && setMessage('Invitations are temporarily unavailable. Please try again.'));
        }
        return () => { current = false; };
    }, [eligible, freeCommunity, schoolId, user?.schoolId, user?.id, user?.roleId]);

    if (!eligible) { return <>{back}<p>Invitations are not available in this community.</p></>; }
    if (!referrals || referrals.scope !== activeScope.current) { return <>{back}<p role='status'>{message || 'Loading invitations…'}</p></>; }
    if (!referrals.enabled || !referrals.roles?.length) { return <>{back}<p>Invitations are not available in this community.</p></>; }
    return <>
        {back}
        {referrals?.enabled && !!referrals.roles?.length && <Panel aria-label='Invite your friends'>
            <h1>Invite your friends</h1>
            <p>{freeCommunity ? 'Invite a friend and help your community take shape.' : 'Help your friends find their people and earn points for successful referrals.'}</p>
            <p>Use an email address with a valid school email domain.</p>
            <form onSubmit={async (event) => {

                event.preventDefault();
                const sendingScope = activeScope.current;
                setBusy(true);
                setMessage('');
                try {
                    const result = await referralApi.invite({ email: email.trim(), roleId: Number(roleId) });
                    if (activeScope.current !== sendingScope) { return; }
                    setMessage(result.status === 'Invitation queued' ? '' : result.status);
                    setEmail('');
                    const fresh = await referralApi.referrals();
                    if (activeScope.current === sendingScope) { setReferrals({ ...fresh, scope: sendingScope }); }
                }
                catch (error) { if (activeScope.current === sendingScope) { setMessage(error?.response?.data?.message || 'Unable to invite this address. Please check school eligibility and try again.'); } }
                finally { if (activeScope.current === sendingScope) { setBusy(false); } }
            }}>
                <EmailField><TextField fullWidth variant='filled' label='Friend’s email' type='email' value={email} required inputProps={{ maxLength: 254 }} disabled={busy} onChange={(event) => setEmail(event.target.value)} /></EmailField>
                <RoleField><TextField fullWidth variant='filled' select SelectProps={{ native: true }} label='Invite as' value={roleId} required disabled={busy} onChange={(event) => setRoleId(event.target.value)}>{referrals.roles.map(({ id, label }) => <option key={id} value={id}>{label}</option>)}</TextField></RoleField>
                <Actions><Button variant='contained' color='primary' type='submit' disabled={busy}>{busy ? 'Sending…' : 'Send invitation'}</Button></Actions>
            </form>
            {!!message && <p role='status'>{message}</p>}
            {!!referrals.invitations?.length && <><h3>My invitations</h3><ul>{referrals.invitations.map((invitation) => <li key={invitation.email}>{invitation.email} — {invitation.status}{invitation.rewardedAt ? ` · ${invitation.awardedPoints} points` : ''}</li>)}</ul></>}
        </Panel>}
    </>;
};

internals.Panel = Styled.section`${Ui.surface}
    box-sizing: border-box; width: 100%; max-width: 800px; margin: 24px auto; padding: 20px;
    && h1, && h2 { font-size: 20px; font-weight: 700; margin: 0 0 12px; }
    && h3 { font-size: 16px; } && p, && li, && label { font-size: 16px; line-height: 1.5; }
    img { max-width: 100%; max-height: 240px; object-fit: contain; }
    form { display: flex; flex-wrap: wrap; gap: 16px; align-items: end; }
    ul { padding-left: 20px; }
`;
internals.Actions = Styled.div`
    display: flex; justify-content: center; width: 100%;
`;

internals.EmailField = Styled.div`&& { flex: 1 1 420px; min-width: 0; }`;

internals.RoleField = Styled.div`flex: 0 1 180px; min-width: 0; max-width: 100%;`;
